<script setup>
import {computed} from 'vue';

const GENERIC_EMAIL_DOMAINS = [
    'gmail.com',
    'hotmail.com',
    'yahoo.com',
    'outlook.com',
    'live.com',
    'aol.com',
    'icloud.com',
    'me.com',
    'msn.com',
    'mail.com',
    'inbox.com',
    'gmx.com',
    'zoho.com',
    'yandex.com',
    'protonmail.com',
    'tutanota.com',
    'fastmail.com',
    'hushmail.com',
    'telia.com',
    'bredband.net',
    'bredband2.com',
    'comhem.se',
    'tele2.se',
    'spray.se',
    'passagen.se',
    'live.se',
    'hotmail.se',
    'outlook.se',
    'yahoo.se',
];

const props = defineProps({
    website: {
        type: String,
        default: null,
    },
    email: {
        type: String,
        default: null,
    },
    companyName: {
        type: String,
        default: null,
    },
});

const normalizeDomain = (domain) => {
    if (!domain || typeof domain !== 'string') {
        return null;
    }

    return domain.trim().replace(/^www\./i, '').toLowerCase();
};

const sanitizeWebsiteDomain = (website) => {
    if (!website || typeof website !== 'string') {
        return null;
    }

    let candidate = website.trim();

    if (candidate.length === 0) {
        return null;
    }

    try {
        if (!/^https?:\/\//i.test(candidate)) {
            candidate = `https://${candidate}`;
        }

        const parsed = new URL(candidate);

        if (!parsed.hostname) {
            return null;
        }

        return normalizeDomain(parsed.hostname);
    } catch (error) {
        const manualDomain = candidate.replace(/^https?:\/\//i, '');
        const fallback = manualDomain.split('/')[0];

        return normalizeDomain(fallback);
    }
};

const sanitizeEmailDomain = (email) => {
    if (!email || typeof email !== 'string') {
        return null;
    }

    const [, domain] = email.trim().toLowerCase().split('@');

    if (!domain || domain.length === 0) {
        return null;
    }

    if (GENERIC_EMAIL_DOMAINS.includes(domain)) {
        return null;
    }

    return normalizeDomain(domain);
};

const sanitizedDomain = computed(() => {
    const websiteDomain = sanitizeWebsiteDomain(props.website);

    if (websiteDomain) {
        return websiteDomain;
    }

    return sanitizeEmailDomain(props.email);
});

const logoUrl = computed(() => {
    if (!sanitizedDomain.value) {
        return null;
    }

    return `https://img.logo.dev/${sanitizedDomain.value}?token=pk_ecY6H6QYQRi7BVpPkCKfdg&size=300&retina=true`;
});

const altText = computed(() => {
    if (!props.companyName) {
        return 'Company logo';
    }

    return `${props.companyName} logo`;
});
</script>

<template>
    <img
        v-if="logoUrl"
        :src="logoUrl"
        :alt="altText"
    />
</template>
